/* Adapted from Tyler Heshka's fetch-weather.js v2.0 (2026-04-11).
 * Commercial. Licensed to Central Botanical Growers Ltd. o/a Floral Acres.
 * GeoMet city-page weather; no credentials or customer data involved. */
(() => {
  if (customElements.get('fa-weather')) return;
  const cache = new Map();
  const ttl = 5 * 60 * 1000;
  function load(city) {
    const saved = cache.get(city);
    if (saved && Date.now() - saved.time < ttl) return saved.promise;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    const promise = fetch(`https://api.weather.gc.ca/collections/citypageweather-realtime/items/${encodeURIComponent(city)}?f=json&lang=en`, { signal: controller.signal })
      .then(response => {
        if (!response.ok) throw new Error('Weather request failed');
        return response.json();
      })
      .then(data => {
        if (!data?.properties?.currentConditions) throw new Error('Unexpected weather data');
        return data.properties;
      })
      .catch(error => { cache.delete(city); throw error; })
      .finally(() => clearTimeout(timer));
    cache.set(city, { time: Date.now(), promise });
    return promise;
  }
  const en = value => value?.en ?? '';
  const temperature = item => {
    const value = en(item?.value);
    return value === '' || value === null ? '' : `${value}°${en(item?.units) || 'C'}`;
  };
  class FAWeather extends HTMLElement {
    connectedCallback() {
      this.active = true;
      this.refresh();
      this.interval = setInterval(() => this.refresh(), ttl);
    }
    disconnectedCallback() { this.active = false; clearInterval(this.interval); }
    field(name) { return this.querySelector(`[data-weather-field="${name}"]`); }
    text(name, value) {
      const el = this.field(name);
      if (el) { el.textContent = value; el.hidden = !value; }
    }
    async refresh() {
      if (this.loading) return;
      this.loading = true;
      try {
        const properties = await load(this.dataset.city || 'sk-40');
        if (this.active) this.render(properties);
      } catch {
        if (this.active) this.unavailable();
      } finally { this.loading = false; }
    }
    unavailable() {
      this.text('status', 'Weather data is temporarily unavailable. Use the Environment Canada link below.');
      ['current', 'observed', 'forecasts', 'alerts', 'image'].forEach(name => this.field(name)?.replaceChildren());
    }
    render(properties) {
      const current = properties.currentConditions;
      const observed = new Date(en(current.timestamp));
      const validTime = Number.isFinite(observed.getTime());
      const stale = !validTime || Date.now() - observed.getTime() > 6 * 60 * 60 * 1000;
      this.text('status', stale ? 'Latest observation is delayed or unavailable; check Environment Canada for current conditions and alerts.' : '');
      this.text('current', [en(current.condition), temperature(current.temperature)].filter(Boolean).join(' · ') || 'Current conditions unavailable.');
      const time = validTime ? new Intl.DateTimeFormat('en-CA', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Regina', timeZoneName: 'short'
      }).format(observed) : '';
      this.text('observed', [en(current.station?.value), time].filter(Boolean).join(' · '));
      const image = this.field('image');
      if (image) {
        image.replaceChildren();
        try {
          const url = new URL(current.iconCode?.url);
          if (url.protocol === 'https:' && url.hostname === 'weather.gc.ca') {
            const img = document.createElement('img');
            img.src = url.href; img.alt = ''; img.width = 56; img.height = 56;
            img.addEventListener('error', () => img.remove(), { once: true });
            image.append(img);
          }
        } catch { /* Missing or unsupported provider icon; text remains available. */ }
      }
      const forecasts = this.field('forecasts');
      if (forecasts) {
        forecasts.replaceChildren();
        // Preserve the provider's real period names: Tonight / Sunday, not guessed day/night slots.
        for (const forecast of (properties.forecastGroup?.forecasts || []).slice(0, 2)) {
          const label = en(forecast.period?.textForecastName);
          const summary = en(forecast.abbreviatedForecast?.textSummary);
          const temps = (forecast.temperatures?.temperature || []).map(item => {
            const value = temperature(item);
            return value ? `${en(item.class)} ${value}`.trim() : '';
          }).filter(Boolean);
          const text = [summary, ...temps].filter(Boolean).join('. ');
          if (text) { const p = document.createElement('p'); p.textContent = `${label ? label + ': ' : ''}${text}`; forecasts.append(p); }
        }
      }
      const alerts = this.field('alerts');
      if (alerts) {
        alerts.replaceChildren();
        if (!Array.isArray(properties.warnings) || stale) {
          alerts.textContent = 'Check Environment Canada for current alerts.';
        } else if (!properties.warnings.length) {
          alerts.textContent = 'No alerts reported in the latest weather data.';
        } else {
          for (const warning of properties.warnings) {
            const p = document.createElement('p');
            p.className = 'fa-weather__alert';
            p.textContent = [en(warning.type) || 'Weather alert', en(warning.description)].filter(Boolean).join(': ');
            alerts.append(p);
          }
        }
      }
    }
  }
  customElements.define('fa-weather', FAWeather);
})();
